import {
  Add as AddIcon,
  Dangerous as DangerousIcon,
  Edit as EditIcon,
  Inventory as InventoryIcon,
  LocationOn as LocationIcon,
  MeetingRoom as RoomIcon,
  Store as StoreIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  GridLegacy as Grid,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  AdminDialog,
  AdminEmptyState,
  AdminFilterSummary,
  PageShell,
  AdminTableActions,
  AdminTableFrame,
} from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { getApiConfig } from "@/config/api.config";
import { useAuth } from "@/contexts/AuthContext";
import { useTableSorting } from "@/hooks/useTableSorting";
import { Customer, Store as StoreType, StoreService } from "@/services/features/StoreService";

const inventoryApiUrl = getApiConfig().inventory;
const showDeactivateStore = false;

const Stores: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const storeService = StoreService.getInstance();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(null);
  const [storeToDisable, setStoreToDisable] = useState<StoreType | null>(null);
  const [disableConfirmation, setDisableConfirmation] = useState("");
  const [disableError, setDisableError] = useState<string | null>(null);
  const selectedCustomerId = searchParams.get("customerId");
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    stores,
    (store, column) =>
      ({
        id: store.id,
        externalId: store.externalId,
        name: store.name,
        locale: store.locale,
        currency: store.currencyCode,
        timezone: store.timezone,
      })[column],
    "id" as "id" | "externalId" | "name" | "locale" | "currency" | "timezone",
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    document.title = t("management.stores.stores.documentTitle");
    void getCustomers();
  }, [t]);

  const getCustomers = async () => {
    setIsLoading(true);
    try {
      const response = await storeService.getCustomers();
      if (response.data) {
        setCustomers(response.data);
      }
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const getStores = useCallback(
    async (customerId: number) => {
      setIsLoading(true);
      try {
        const response = await storeService.getStoresByCustomer(customerId);
        if (response.data) {
          setStores(response.data);
        }
      } catch {
      } finally {
        setIsLoading(false);
      }
    },
    [storeService],
  );

  useEffect(() => {
    if (!selectedCustomerId || customers.length === 0) return;

    const customer = customers.find((entry) => entry.id === Number(selectedCustomerId));
    if (customer && customer.id !== currentCustomer?.id) {
      setCurrentCustomer(customer);
      void getStores(customer.id);
    }
  }, [currentCustomer?.id, customers, getStores, selectedCustomerId]);

  const handleCustomerChange = (newValue: Customer | null) => {
    setCurrentCustomer(newValue);
    setStores([]);
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);

      if (newValue) {
        nextParams.set("customerId", String(newValue.id));
      } else {
        nextParams.delete("customerId");
      }

      return nextParams;
    });

    if (newValue) {
      void getStores(newValue.id);
    }
  };

  const goToAdd = () => {
    if (currentCustomer) {
      navigate(`/admin/organization/stores/add/${currentCustomer.id}/${currentCustomer.name}`, {
        state: { returnTo: location.pathname + location.search },
      });
    }
  };

  const goToDetails = (storeId: number) => {
    void navigate(`/admin/organization/stores/${storeId}`, {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  };

  const goToEdit = (storeId: number) => {
    navigate(`/admin/organization/stores/edit/${storeId}`, {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  };

  const goToEditChangingRooms = (storeId: number) => {
    navigate(`/admin/organization/stores/edit-rooms/${storeId}`, {
      state: { returnTo: location.pathname + location.search },
    });
  };

  const openInventory = (storeId: number) => {
    window.open(`${inventoryApiUrl}/sample/${storeId}`, "_blank", "noopener,noreferrer");
  };

  const openDisableDialog = (store: StoreType) => {
    setStoreToDisable(store);
    setDisableConfirmation("");
    setDisableError(null);
  };

  const closeDisableDialog = () => {
    if (isLoading) return;
    setStoreToDisable(null);
    setDisableConfirmation("");
    setDisableError(null);
  };

  const handleDisableStore = async () => {
    if (!storeToDisable || disableConfirmation.trim() !== String(storeToDisable.id)) return;

    setIsLoading(true);
    setDisableError(null);
    const response = await storeService.disableStoreById(storeToDisable.id);

    if (response.success) {
      enqueueSnackbar(t("management.stores.stores.notifications.storeDisabled"), {
        variant: "success",
      });
      setStoreToDisable(null);
      setDisableConfirmation("");
      if (currentCustomer) {
        await getStores(currentCustomer.id);
      }
    } else {
      setDisableError(response.error || t("management.stores.stores.errors.failedDisableStore"));
    }

    setIsLoading(false);
  };

  const renderMobileView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {stores.length > 0 ? (
        stores.map((store) => (
          <Card key={store.id}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              {/* Store Header */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {store.name}
                  </Typography>
                </Stack>
                <Stack alignItems="flex-end" spacing={0.25}>
                  <Typography variant="caption" color="text.secondary">
                    {t("management.stores.stores.fields.id")}
                  </Typography>
                  <Typography variant="body2">{store.id}</Typography>
                </Stack>
              </Stack>

              {/* Store Details */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("management.stores.stores.fields.externalId")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {store.externalId || t("management.stores.stores.empty.unset")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("management.stores.stores.fields.locale")}
                  </Typography>
                  <Chip
                    label={store.locale}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("management.stores.stores.fields.currency")}
                  </Typography>
                  <Chip
                    label={store.currencyCode}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("management.stores.stores.fields.timezone")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {store.timezone}
                  </Typography>
                </Grid>
              </Grid>

              {/* Actions */}
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => goToDetails(store.id)}
                  disabled={isLoading}
                >
                  {t("commandCenter.actions.view")}
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => goToEdit(store.id)}
                  disabled={isLoading}
                >
                  {t("management.stores.stores.actions.edit")}
                </Button>
                <Button
                  size="small"
                  startIcon={<RoomIcon />}
                  onClick={() => goToEditChangingRooms(store.id)}
                  disabled={isLoading}
                >
                  {t("management.stores.stores.actions.rooms")}
                </Button>
                <Button
                  size="small"
                  startIcon={<InventoryIcon />}
                  onClick={() => openInventory(store.id)}
                  disabled={isLoading}
                >
                  {t("management.stores.stores.actions.inventory")}
                </Button>
                {showDeactivateStore && store.active && (
                  <Button
                    size="small"
                    startIcon={<DangerousIcon />}
                    onClick={() => openDisableDialog(store)}
                    disabled={isLoading}
                    color="error"
                  >
                    {t("management.stores.stores.actions.disable")}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card>
          <CardContent sx={{ textAlign: "center" }}>
            <AdminEmptyState
              icon={<StoreIcon sx={{ fontSize: 40 }} />}
              text={
                currentCustomer
                  ? t("management.stores.stores.empty.noStores")
                  : t("management.stores.stores.empty.selectCustomer")
              }
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );

  if (authLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.stores.stores.title")}
      icon={<StoreIcon />}
      titleAccessory={<LoadingRing loading={isLoading} />}
      headerActions={
        <Button
          variant="contained"
          onClick={goToAdd}
          disabled={!currentCustomer || isLoading}
          startIcon={<AddIcon />}
        >
          {t("management.stores.stores.actions.addNewStore")}
        </Button>
      }
    >
      <AdminFilterSummary
        filter={
          <Box sx={{ position: "relative" }}>
            <MuiAutocompleteMultiSelect
              label={t("management.stores.stores.sections.selectCustomer")}
              options={customers}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              value={currentCustomer ? [currentCustomer] : []}
              onChange={(next) => handleCustomerChange(next[0] ?? null)}
              loading={isLoading}
              disabled={isLoading}
            />
          </Box>
        }
        count={stores.length}
        countLabel={t("management.stores.stores.fields.totalStores")}
        icon={<StoreIcon color="primary" sx={{ fontSize: 40 }} />}
      />

      {/* Stores Table/Cards */}
      <Box>
        {isMobile ? (
          renderMobileView()
        ) : (
          <AdminTableFrame>
            <TableHead>
              <TableRow>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="id"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("management.stores.stores.fields.id")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="externalId"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("management.stores.stores.fields.externalId")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="name"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("management.stores.stores.fields.storeName")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="locale"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("management.stores.stores.fields.locale")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="currency"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("management.stores.stores.fields.currency")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="timezone"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("management.stores.stores.fields.timezone")}
                </SortableTableCell>
                <TableCell align="right">{t("management.stores.stores.fields.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stores.length > 0 ? (
                sortedItems.map((store) => (
                  <TableRow
                    key={store.id}
                    hover
                    role="button"
                    tabIndex={0}
                    onClick={() => goToDetails(store.id)}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) {
                        return;
                      }

                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        goToDetails(store.id);
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      "&&:hover, &&:hover > .MuiTableCell-root": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <TableCell>{store.id}</TableCell>
                    <TableCell>
                      {store.externalId || t("management.stores.stores.empty.unset")}
                    </TableCell>
                    <TableCell>{store.name}</TableCell>
                    <TableCell>
                      {store.locale ? (
                        <Chip
                          label={store.locale}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        t("management.stores.stores.empty.unset")
                      )}
                    </TableCell>
                    <TableCell>
                      {store.currencyCode ? (
                        <Chip
                          label={store.currencyCode}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        t("management.stores.stores.empty.unset")
                      )}
                    </TableCell>
                    <TableCell>{store.timezone}</TableCell>
                    <TableCell align="right">
                      <AdminTableActions
                        actions={[
                          {
                            icon: <VisibilityIcon />,
                            hint: t("commandCenter.actions.view"),
                            action: () => goToDetails(store.id),
                            disabled: isLoading,
                          },
                          {
                            icon: <EditIcon />,
                            hint: t("management.stores.stores.tooltips.editStore"),
                            action: () => goToEdit(store.id),
                            disabled: isLoading,
                          },
                          {
                            icon: <RoomIcon />,
                            hint: t("management.stores.stores.tooltips.editRooms"),
                            action: () => goToEditChangingRooms(store.id),
                            disabled: isLoading,
                          },
                          {
                            icon: <InventoryIcon />,
                            hint: t("management.stores.stores.tooltips.viewInventory"),
                            action: () => openInventory(store.id),
                            disabled: isLoading,
                          },
                          ...(showDeactivateStore && store.active
                            ? [
                                {
                                  icon: <DangerousIcon />,
                                  hint: t("management.stores.stores.tooltips.disableStore"),
                                  action: () => openDisableDialog(store),
                                  disabled: isLoading,
                                  color: "error" as const,
                                },
                              ]
                            : []),
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <AdminEmptyState
                      icon={<StoreIcon sx={{ fontSize: 40 }} />}
                      text={
                        currentCustomer
                          ? t("management.stores.stores.empty.noStores")
                          : t("management.stores.stores.empty.selectCustomer")
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </AdminTableFrame>
        )}
      </Box>
      <AdminDialog
        open={Boolean(storeToDisable)}
        onClose={closeDisableDialog}
        title={t("management.stores.stores.disableDialog.title")}
        icon={<DangerousIcon />}
        error={disableError}
        cancelAction={{
          label: t("common.cancel"),
          onClick: closeDisableDialog,
          disabled: isLoading,
        }}
        primaryAction={{
          label: t("management.stores.stores.actions.disable"),
          onClick: () => void handleDisableStore(),
          color: "error",
          loading: isLoading,
          disabled: !storeToDisable || disableConfirmation.trim() !== String(storeToDisable.id),
        }}
      >
        <Stack spacing={2}>
          <Alert severity="warning">
            <Trans
              i18nKey="management.stores.stores.deactivateDialog.warning"
              values={{ storeName: storeToDisable?.name }}
              components={{
                storeName: <Box component="span" sx={{ fontStyle: "italic" }} />,
              }}
            />
          </Alert>
          <Typography>
            <Trans
              i18nKey="management.stores.stores.deactivateDialog.confirmation"
              values={{ storeId: storeToDisable?.id }}
              components={{ storeId: <Box component="span" sx={{ fontWeight: 700 }} /> }}
            />
          </Typography>
          <TextField
            fullWidth
            label={t("management.stores.stores.disableDialog.storeIdLabel")}
            value={disableConfirmation}
            onChange={(event) => setDisableConfirmation(event.target.value)}
            disabled={isLoading}
            inputProps={{ inputMode: "numeric" }}
          />
        </Stack>
      </AdminDialog>
    </PageShell>
  );
};

export default Stores;
